import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ControlAction } from '../../controlCenterMeta';
import type { ActionRefusals } from '../../useControlCenterPage';
import { ActionRefusalPopover } from '../actionRefusal';

type RecentActionsProps = {
    actions: ControlAction[];
    refusals: ActionRefusals;
    onRun: (action: ControlAction) => void;
};

type RecentChipProps = {
    action: ControlAction;
    refusals: ActionRefusals;
    onRun: (action: ControlAction) => void;
};

/**
 * What was reached for last — a row of chips between the pinned grid and the
 * catalog.
 *
 * Buttons rather than cards: these are the same actions the grid above already
 * shows as cards, and a second row of cards would read as a second pinned
 * grid. A chip is a shortcut to something, not a thing.
 *
 * This is the page teaching its own shortcuts: run something out of the catalog
 * or the palette and it appears here, one row above where it was found. Dropped
 * entirely on a phone, where the pinned grid already covers the same intent in
 * the space a second row would take.
 */
export const RecentActions: React.FC<RecentActionsProps> = ({
    actions,
    refusals,
    onRun,
}) => {
    const { t } = useTranslation('pages');

    if (actions.length === 0) {
        return null;
    }

    return (
        <section
            aria-label={t('controlCenter.recent.title')}
            className="hidden flex-wrap items-center gap-2.5 sm:flex"
            {...getNodeTestIdProp('control-center-recent')}
        >
            <span className="text-xs font-medium text-muted">
                {t('controlCenter.recent.title')}
            </span>
            {actions.map((action) => (
                <RecentChip
                    action={action}
                    key={action.id}
                    onRun={onRun}
                    refusals={refusals}
                />
            ))}
        </section>
    );
};

/**
 * One chip.
 *
 * A refused chip uses the popover's `trigger="child"` form: a HeroUI `Button`
 * consumes the trigger context itself, and wrapping one in `Popover.Trigger`
 * would put a button inside that trigger's own `role="button"`.
 *
 * This is the fourth place an action can be pressed, after the pinned grid, the
 * catalog and the palette — and the easiest of the four to forget, which is
 * exactly why a press here has to refuse like the others rather than do
 * nothing.
 */
const RecentChip: React.FC<RecentChipProps> = ({ action, refusals, onRun }) => {
    const { t } = useTranslation('pages');
    const refusal = refusals.refusalFor(action.id);

    const chip = (
        <Button
            onPress={refusal ? undefined : () => onRun(action)}
            size="sm"
            variant="outline"
            {...getNodeTestIdProp(`control-center-recent-chip-${action.id}`)}
        >
            <Icon aria-hidden icon={action.icon} />
            {t(`controlCenter.actions.${action.id}`)}
        </Button>
    );

    return refusal ? (
        <ActionRefusalPopover action={action} refusal={refusal} trigger="child">
            {chip}
        </ActionRefusalPopover>
    ) : (
        chip
    );
};
